import { Lesson } from "../../types/education";

// Import all lesson markdown files
// Note: These are imported as raw strings to avoid Vite parsing issues
import colorBasicsLesson from "../../education/lessons/color-basics-lesson.md?raw";
import colorWheelLesson from "../../education/lessons/color-wheel-lesson.md?raw";
import understandingColorHarmonyLesson from "../../education/lessons/understanding-color-harmony.md?raw";
import basicColorSchemesLesson from "../../education/lessons/basic-color-schemes.md?raw";
import introductionToContrastLesson from "../../education/lessons/introduction-to-contrast.md?raw";
import colorPsychologyBasicsLesson from "../../education/lessons/color-psychology-basics.md?raw";
import digitalVsPrintColorLesson from "../../education/lessons/digital-vs-print-color.md?raw";
import accessibilityFundamentalsLesson from "../../education/lessons/accessibility-fundamentals.md?raw";

// Map lesson IDs to their markdown content
export const lessonContentMap: Record<string, string> = {
  "color-basics": colorBasicsLesson,
  "color-wheel": colorWheelLesson,
  "understanding-color-harmony": understandingColorHarmonyLesson,
  "basic-color-schemes": basicColorSchemesLesson,
  "introduction-to-contrast": introductionToContrastLesson,
  "color-psychology-basics": colorPsychologyBasicsLesson,
  "digital-vs-print-color": digitalVsPrintColorLesson,
  "accessibility-fundamentals": accessibilityFundamentalsLesson,
};

// Get lesson content by ID
export const getLessonContent = (lessonId: string): string | null => {
  return lessonContentMap[lessonId] || null;
};

// Check if lesson has content
export const hasLessonContent = (lessonId: string): boolean => {
  return lessonId in lessonContentMap;
};

// Get all lessons with content
export const getLessonsWithContent = (lessons: Lesson[]): Lesson[] => {
  return lessons.filter((lesson) => hasLessonContent(lesson.id));
};

// Parse lesson metadata from markdown content
export const parseLessonMetadata = (content: string): Partial<Lesson> => {
  const metadata: Partial<Lesson> = {};

  // Extract duration
  const durationMatch = content.match(
    /\*\*Estimated Time\*\*:\s*(\d+)\s*minutes/,
  );
  if (durationMatch) {
    metadata.duration = durationMatch[1];
  }

  // Extract difficulty
  const difficultyMatch = content.match(/\*\*Difficulty\*\*:\s*(\w+)/);
  if (difficultyMatch) {
    metadata.difficulty = difficultyMatch[1].toLowerCase() as
      | "beginner"
      | "intermediate"
      | "advanced";
  }

  // Extract category
  const categoryMatch = content.match(/\*\*Category\*\*:\s*(\w+)/);
  if (categoryMatch) {
    metadata.category = categoryMatch[1];
  }

  // Extract objectives
  const objectivesMatch = content.match(
    /## Learning Objectives\n\nBy the end of this lesson, students will be able to:\n\n(.*?)\n\n##/s,
  );
  if (objectivesMatch) {
    const objectivesText = objectivesMatch[1];
    const objectives = objectivesText
      .split("\n")
      .filter((line) => line.trim().startsWith("-"))
      .map((line) => line.replace(/^-\s*/, "").trim());
    metadata.objectives = objectives;
  }

  return metadata;
};

// Extract interactive elements from lesson content
export const extractInteractiveElements = (content: string): string[] => {
  const interactiveElements: string[] = [];
  const sections = content.split(/###\s+/);

  sections.forEach((section) => {
    if (section.includes("Interactive Element:")) {
      const titleMatch = section.match(/Interactive Element:\s*(.+)/);
      if (titleMatch) {
        interactiveElements.push(titleMatch[1].trim());
      }
    }
  });

  return interactiveElements;
};

// Extract quiz questions from lesson content
export const extractQuizQuestions = (content: string): any[] => {
  const questions: any[] = [];
  const quizSection = content.match(
    /### Page \d+: Practice Quiz[\s\S]*?## Assessment Criteria/,
  );

  if (quizSection) {
    // This is a simplified extraction - in a real implementation,
    // you'd want more sophisticated parsing
    const questionMatches = quizSection[0].match(
      /\d+\.\s+(.+?)(?=\n\d+\.|\n\n|$)/g,
    );

    questionMatches?.forEach((match, index) => {
      questions.push({
        id: `q${index + 1}`,
        question: match.trim(),
        type: "multiple-choice",
        options: [], // Would need more sophisticated parsing
        correctAnswer: "",
        explanation: "",
      });
    });
  }

  return questions;
};
